import {
  AirportEntity, CompanyEntity, PlaneCalculatorSettingCategoryEntity,
  PlaneCalculatorSettingEntity,
  PlaneCalculatorSettingSubCategoryEntity,
  PlaneCalculatorSettingTypeEntity,
  PlaneCategoryEntity,
  PlaneEntity,
  PlaneShortEntity,
  TimezoneEntity
} from "../../../proto/generated/api_entities_pb";
import {
  IAirport,
  ICompany,
  IFlyHourCalculatorCategory,
  IFlyHourCalculatorSetting,
  IFlyHourCalculatorSettingType,
  IPlane,
  IPlaneCategory,
  IPlaneShort
} from "./fleetReducerTypes";
import {UploadFile} from "antd/es/upload";
import {parseStringAsUTCDate} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {ITimezone} from "../userReducer/userReducerTypes";
import {DisplayTimezoneOffset} from "../../../utils/helpers/textDisplayHelpers/DisplayTimezoneOffset";
import {
  FlyHourPriceCalculatorSettingTypeEnum,
  FlyHourPriceDefaultCategoryEnum,
  FlyHourPriceDefaultSettingTypeEnum,
  FlyHourPriceDefaultSubcategoryEnum
} from "../../../pages/Lk/OperatorsOnly/Fleet/components/flyHourPriceCalculator/types/flyHourPriceSettingEnums";
import isNumeric from "antd/es/_util/isNumeric";
import {Logger} from "../../../utils/logger/Logger";


export const mapPlaneEntityToIPlane = (item: PlaneEntity): IPlane => {
  const planeType = item.getPlanetype();
  if (!planeType)
    throw new Error("Null PlaneTypeEntity in received PlaneEntity");

  const planeCategory = planeType.getPlanecategory();
  if (!planeCategory)
    throw new Error("Null PlaneCategoryEntity in received PlaneEntity -> PlaneTypeEntity");

  const baseAirport = item.getBaseairport();
  if (!baseAirport)
    throw new Error("Null AirportEntity in received PlaneEntity");

  const insurance = item.getInsurance();
  if (!insurance)
    throw new Error("Null insurance in received PlaneEntity");

  const insuranceFile: UploadFile = {
    uid: insurance.getFileid(),
    name: insurance.getName(),
    fileName: insurance.getName(),
    url: insurance.getFilepath(),
  }

  const certificate = item.getCertificate();
  if (!certificate)
    throw new Error("Null certificate in received PlaneEntity");

  const certificateFile: UploadFile = {
    uid: certificate.getFileid(),
    name: certificate.getName(),
    fileName: certificate.getName(),
    url: certificate.getFilepath(),
  }

  const etops = item.getEtops();
  const etopsFile: UploadFile | undefined = etops ? {
    uid: etops.getFileid(),
    name: etops.getName(),
    fileName: etops.getName(),
    url: etops.getFilepath(),
  } : undefined;

  const currency = item.getCurrency();
  if (!currency)
    throw new Error("Null CurrencyEntity (currency) in received PlaneEntity");

  const calculatorCategories = mapPlaneCalculatorSettingEntityListToIFlyHourCalculatorCategoryList(item.getPlanecalculatorsettingsList());

  return {
    id: item.getPlaneid(),
    planeType: {
      id: planeType.getPlanetypeid(),
      name: planeType.getName(),
      planeCategory: {
        id: planeCategory.getPlanecategoryid(),
        name: planeCategory.getName(),
      }
    },
    baseAirport: mapAirportEntityToIAirport(baseAirport),
    registrationNumber: item.getRegistrationnumber(),
    manufactureDate: parseStringAsUTCDate(item.getManufacturedate()),
    renovationDate: parseStringAsUTCDate(item.getRenovationdate()),
    maxPassengers: item.getMaxpassengers(),
    maxDistance: item.getMaxdistance(),
    isAnimals: item.getIsanimals(),
    isCargo: item.getIscargo(),
    isSmoking: item.getIssmoking(),
    isAmbulance: item.getIsambulance(),
    isWifi: item.getIswifi(),
    isOvenAboard: item.getIsovenaboard(),
    description: item.getDescription(),
    imageFiles: item.getImagesList().map(f => {
      return {
        uid: f.getFileid(),
        name: f.getName(),
        fileName: f.getName(),
        url: f.getFilepath(),
      }
    }),
    insuranceFile: insuranceFile,
    certificateFile: certificateFile,
    etopsFile: etopsFile,
    flyHourCurrency: {
      id: currency.getCurrencyid(),
      name: currency.getName(),
    },
    flyHourPrice: item.getFlyhourprice(),
    calculatorCategories: calculatorCategories,
    planeStatusId: item.getPlanestatusid() || 1,
    validUntil: item.getValiduntil() || undefined,
    // International Flights documents from operator company
    // These will be populated from the operator's company data (if available in API response)
    operatorCard: undefined,
    registrationCertificate: undefined,
    caaOperatorApproval: undefined,
    easaTcoAuthorisation: undefined,
    faaAuthorization: undefined,
    flyCompany: (() => {
      const flyCompany = item.getFlycompany();
      return flyCompany ? mapCompanyEntityToICompany(flyCompany) : undefined;
    })(),
  }
}


export const mapPlaneShortEntityToIPlaneShort = (item: PlaneShortEntity): IPlaneShort => {
  return {
    id: item.getPlaneid(),
    registrationNumber: item.getRegistrationnumber(),
    planeTypeName: item.getPlanetypename() || undefined,
  }
}


export const mapPlaneCategoryEntityToIPlaneCategory = (item: PlaneCategoryEntity): IPlaneCategory => {
  return {
    id: item.getPlanecategoryid(),
    name: item.getName(),
  }
}


export const mapPlaneCategoryIdToIPlaneCategory = (item: number): PlaneCategoryEntity => {
  return new PlaneCategoryEntity()
    .setPlanecategoryid(item)
}


export const mapAirportEntityToIAirport = (item: AirportEntity): IAirport => {
  const timezone = item.getTimezone();
  if (!timezone)
    throw new Error("Null TimezoneEntity (timezone) in received AirportEntity");

  return {
    id: item.getAirportid(),
    name: item.getName(),
    icaoCode: item.getIcaocode(),
    iataCode: item.getIatacode(),
    countryName: item.getCountryname(),
    latitude: item.getLatitude(),
    longitude: item.getLongitude(),
    timezone: mapTimeZoneEntityToITimezone(timezone),
  }
}


export const mapTimeZoneEntityToITimezone = (item: TimezoneEntity): ITimezone => {
  const timezoneLabel = `${item.getName()} ${DisplayTimezoneOffset(item.getOffset())}`;

  return {
    id: item.getTimezoneid(),
    name: timezoneLabel,
    offset: item.getOffset(),
  }
}

export const mapCompanyEntityToICompany = (item: CompanyEntity): ICompany => {
  return {
    id: item.getCompanyid(),
    name: item.getCompanyname(),
    email: item.getEmail() || undefined,
    phone: item.getPhone() || undefined,
    address: item.getAddress() || undefined,
    website: item.getWebsite() || undefined,
  }
}


export const mapPlaneCalculatorSettingEntityListToIFlyHourCalculatorCategoryList = (planeCalculatorSettingEntities: PlaneCalculatorSettingEntity[]): IFlyHourCalculatorCategory[] => {
  let categories: IFlyHourCalculatorCategory[] = []

  planeCalculatorSettingEntities.forEach(setting => {
    const settingType = setting.getPlanecalculatorsettingtype();
    if (!settingType)
      throw new Error("Null PlaneCalculatorSettingTypeEntity (planeCalculatorSettingType) in received PlaneEntity -> SettingEntity");

    const subcategory = settingType.getPlanecalculatorsettingsubcategory();
    if (!subcategory)
      throw new Error("Null PlaneCalculatorSettingSubcategoryEntity (planeCalculatorSettingSubcategory) in received PlaneEntity -> SettingEntity -> PlaneCalculatorSettingTypeEntity");

    const category = subcategory.getPlanecalculatorsettingcategory();
    if (!category)
      throw new Error("Null PlaneCalculatorSettingCategoryEntity (planeCalculatorSettingCategory) in received PlaneEntity -> SettingEntity -> PlaneCalculatorSettingTypeEntity -> PlaneCalculatorSettingSubcategoryEntity");

    const noSuchCategory = categories.every(c => c.keyName !== category.getName());
    if (noSuchCategory) {
      categories.push({
        id: category.getPlanecalculatorsettingcategoryid(),
        keyName: category.getName(),
        label: category.getName(),
        isDefault: category.getIsdefault(),
        subcategories: []
      });
    }

    const noSuchSubcategory = categories.map(c => c.subcategories).flat().every(sc => sc.keyName !== subcategory.getName());
    if (noSuchSubcategory) {
      categories = categories.map(c => {
        if (c.keyName !== category.getName())
          return c;

        const newSubcategory = {
          id: subcategory.getPlanecalculatorsettingsubcategoryid(),
          keyName: subcategory.getName(),
          label: subcategory.getName(),
          isDefault: subcategory.getIsdefault(),
          settingTypes: [],
        }
        return {...c, subcategories: [...c.subcategories, newSubcategory]}
      });
    }

    // For seasonal coefficients, we allow multiple settings with the same type name (periods with different dates)
    // For other settings, we only add if there's no setting with that type name yet
    const isSeasonalCoefficient = settingType.getName() === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES 
      || settingType.getName() === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON;
    const hasDates = setting.hasDatefrom() && setting.getDatefrom() && setting.hasDateto() && setting.getDateto();
    
    // Determine if we should add this setting
    let shouldAdd = false;
    if (isSeasonalCoefficient && hasDates) {
      // Always add periods (they have dates and are unique)
      shouldAdd = true;
    } else if (isSeasonalCoefficient && !hasDates) {
      // Only add main field if no main field exists yet (check for same type name without dates)
      const existingSettings = categories.map(c => c.subcategories).flat().map(sc => sc.settingTypes).flat();
      const mainFieldExists = existingSettings.some(st => 
        st.keyName === settingType.getName() && !st.setting.dateFrom && !st.setting.dateTo
      );
      shouldAdd = !mainFieldExists;
    } else {
      // For other settings, only add if no setting with that type exists yet
      const existingSettings = categories.map(c => c.subcategories).flat().map(sc => sc.settingTypes).flat();
      shouldAdd = existingSettings.every(st => st.keyName !== settingType.getName());
    }
    
    if (shouldAdd) {
      categories = categories.map(c => {
        return {
          ...c, subcategories: [...c.subcategories.map(sc => {
            if (sc.keyName !== subcategory.getName())
              return sc;

            const isValueNumeric = isNumeric(setting.getValue());
            if (!isValueNumeric)
              Logger.error(`Got not numeric value ${setting.getValue()} for key: ${settingType.getName()}`)

            const settingData: IFlyHourCalculatorSetting = {
              id: setting.getPlanecalculatorsettingid(),
              value: isValueNumeric ? Number(setting.getValue()) : 0,
              specificType: setting.getType() !== 0 ? setting.getType() : undefined,
            };

            // Read additional fields safely
            if (setting.hasFromhours()) {
              settingData.fromHours = setting.getFromhours();
            }
            if (setting.hasTohours()) {
              settingData.toHours = setting.getTohours();
            }
            if (setting.hasDatefrom() && setting.getDatefrom()) {
              settingData.dateFrom = setting.getDatefrom();
            }
            if (setting.hasDateto() && setting.getDateto()) {
              settingData.dateTo = setting.getDateto();
            }
            if (setting.hasFuelgallonsperhour()) {
              settingData.fuelGallonsPerHour = setting.getFuelgallonsperhour();
            }

            // For periods, create a unique keyName to distinguish them
            let keyName = settingType.getName();
            if (isSeasonalCoefficient && hasDates) {
              // Create a unique keyName for periods using the setting ID and dates
              keyName = `${settingType.getName()}_period_${setting.getPlanecalculatorsettingid()}`;
            }

            const newSettingType: IFlyHourCalculatorSettingType = {
              id: settingType.getPlanecalculatorsettingstypeid(),
              keyName: keyName,
              label: settingType.getName(),
              isEnabled: settingType.getIsenable(),
              isDefault: settingType.getIsdefault(),
              setting: settingData,
              type: settingType.getType(),
            }
            return {...sc, settingTypes: [...sc.settingTypes, newSettingType]};
          })]
        }
      });
    }
  });

  return categories;
}


export const mapIFlyHourCalculatorCategoriesToPlaneCalculatorSettingEntities = (flyHoursCount: number, categories: IFlyHourCalculatorCategory[]) => {
  const settingEntities: PlaneCalculatorSettingEntity[] = [];

  settingEntities.push(new PlaneCalculatorSettingEntity()
    .setValue(flyHoursCount.toString())
    .setPlanecalculatorsettingtype(new PlaneCalculatorSettingTypeEntity()
      .setName(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_BASE_FLY_HOURS)
      .setIsenable(true)
      .setIsdefault(true)
      .setType(FlyHourPriceCalculatorSettingTypeEnum.FLAT)
      .setPlanecalculatorsettingsubcategory(new PlaneCalculatorSettingSubCategoryEntity()
        .setName(FlyHourPriceDefaultSubcategoryEnum.SUB_CATEGORY_BASE)
        .setIsdefault(true)
        .setPlanecalculatorsettingcategory(new PlaneCalculatorSettingCategoryEntity()
          .setName(FlyHourPriceDefaultCategoryEnum.CATEGORY_BASE)
          .setIsdefault(true)
        )
      )
    ))

  categories.forEach(c => {
    c.subcategories.forEach(sc => {
      sc.settingTypes.forEach(st => {
        // Отправляем запись, если есть значение (включая 0) ИЛИ есть дополнительные поля (даты, часы, топливо)
        // Значение 0 тоже валидно для коэффициентов и других параметров
        const hasValue = st.setting.value !== undefined && st.setting.value !== null;
        const hasAdditionalFields = 
          st.setting.fromHours !== undefined || 
          st.setting.toHours !== undefined || 
          st.setting.dateFrom !== undefined || 
          st.setting.dateTo !== undefined || 
          st.setting.fuelGallonsPerHour !== undefined;
        
        if (hasValue || hasAdditionalFields) {
          // Определяем тип: сначала из specificType, если есть, иначе из type
          const settingType = st.setting.specificType ?? st.type;
          // Убеждаемся, что тип не 0 (должен быть 1 или 2)
          const finalType = settingType && settingType > 0 ? settingType : FlyHourPriceCalculatorSettingTypeEnum.FLAT;
          
          // Для сезонных коэффициентов с периодами, используем базовое имя типа
          // Периоды имеют keyName вида "parameter_coefficient_low_dates_period_1234567890"
          // но должны использовать базовое имя "parameter_coefficient_low_dates"
          let typeName = st.keyName;
          if (st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES + '_period_')) {
            typeName = FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_LOW_DATES;
          } else if (st.keyName.startsWith(FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON + '_period_')) {
            typeName = FlyHourPriceDefaultSettingTypeEnum.PARAMETER_COEFFICIENT_HIGH_SEASON;
          }
          
          // Для поля топлива, value должен совпадать с fuelGallonsPerHour для ясности
          const isFuelField = typeName === FlyHourPriceDefaultSettingTypeEnum.PARAMETER_CHANGEABLE_FUEL_GALLONS_PER_HOUR;
          const valueToSet = isFuelField && st.setting.fuelGallonsPerHour !== undefined
            ? st.setting.fuelGallonsPerHour.toString()
            : (st.setting.value ?? 0).toString();
          
          const setting = new PlaneCalculatorSettingEntity()
            .setValue(valueToSet)
            .setType(finalType)
            .setPlanecalculatorsettingtype(new PlaneCalculatorSettingTypeEntity()
              .setName(typeName)
              .setIsenable(st.isEnabled)
              .setIsdefault(st.isDefault)
              .setType(finalType)
              .setPlanecalculatorsettingsubcategory(new PlaneCalculatorSettingSubCategoryEntity()
                .setName(sc.keyName)
                .setIsdefault(sc.isDefault)
                .setPlanecalculatorsettingcategory(new PlaneCalculatorSettingCategoryEntity()
                  .setName(c.keyName)
                  .setIsdefault(c.isDefault)
                )
              )
            )
          
          // Добавляем новые поля для блоков маржи
          if (st.setting.fromHours !== undefined) {
            setting.setFromhours(st.setting.fromHours);
          }
          if (st.setting.toHours !== undefined) {
            setting.setTohours(st.setting.toHours);
          }
          
          // Добавляем новые поля для сезонных коэффициентов
          if (st.setting.dateFrom !== undefined) {
            setting.setDatefrom(st.setting.dateFrom);
          }
          if (st.setting.dateTo !== undefined) {
            setting.setDateto(st.setting.dateTo);
          }
          
          // Добавляем поле для топлива
          if (st.setting.fuelGallonsPerHour !== undefined) {
            setting.setFuelgallonsperhour(st.setting.fuelGallonsPerHour);
          }
          
          settingEntities.push(setting)
        }
      })
    })
  })

  return settingEntities;
}